import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { toast } from "sonner";
import { Loader2, Plus, X, Users, Archive, Info } from "lucide-react";

/**
 * Groups: a cohort, for a year.
 *
 * Students churn; worksheets do not. A group is the thing that changes each
 * September, which is why assignments are given to a group rather than to a
 * handful of ticked names.
 *
 * Archiving a group never touches the work its students did. Last year's marks
 * have to survive this year's intake, because showing a parent how the cohorts
 * compare is the point of keeping them.
 */

function Editor({ group, students, onClose, onSaved }) {
  const isNew = !group;
  const [name, setName] = useState(group?.name || "");
  const [year, setYear] = useState(group?.academic_year || "2026/27");
  const [picked, setPicked] = useState(group?.student_ids || []);
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = () => {
    if (!name.trim()) return toast.error("Give the group a name");
    setSaving(true);
    const body = { name, academic_year: year, student_ids: picked };
    const req = isNew ? api.post("/admin/groups", body) : api.patch(`/admin/groups/${group.id}`, body);
    req.then(() => { toast.success(isNew ? "Group created" : "Saved"); onSaved(); onClose(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not save"))
      .finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl w-full max-w-lg my-6 shadow-xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">{isNew ? "New group" : `Edit — ${group.name}`}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {!isNew && (
            <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-2.5">
              <Info size={15} className="mt-0.5 shrink-0 text-blue-600" />
              <p className="text-sm text-blue-900">
                Changing who is in the group affects <strong>future</strong> work only. Anything already given
                out keeps the students it went to, and their marks stay.
              </p>
            </div>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Year 10 — Tuesday"
                className="w-full border border-slate-300 rounded-lg px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="block text-slate-600 mb-1">Academic year</span>
              <input value={year} onChange={(e) => setYear(e.target.value)} placeholder="2026/27"
                className="w-full border border-slate-300 rounded-lg px-3 py-2" />
            </label>
          </div>
          <div>
            <p className="text-sm text-slate-600 mb-1.5">Students — <strong>{picked.length} in this group</strong></p>
            <div className="flex flex-wrap gap-1.5 max-h-[220px] overflow-y-auto">
              {students.map((s) => (
                <button key={s.id} type="button" onClick={() => toggle(s.id)}
                  className={`text-sm rounded-full border px-3 py-1.5 ${
                    picked.includes(s.id) ? "bg-slate-800 text-white border-slate-800" : "border-slate-300 hover:bg-slate-50"}`}>
                  {s.name}{s.year ? ` · Yr ${s.year}` : ""}
                </button>
              ))}
              {students.length === 0 && (
                <p className="text-sm text-slate-400">
                  No student accounts yet. Students appear here once they have signed up.
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-4 py-2 text-sm font-semibold bg-slate-800 text-white rounded-lg disabled:opacity-50">
            {saving ? "Saving…" : isNew ? "Create group" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminGroups() {
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/groups${showArchived ? "?include_archived=true" : ""}`)
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load groups"))
      .finally(() => setLoading(false));
  }, [showArchived]);

  useEffect(() => {
    load();
    api.get("/admin/students?role=student")
      .then((r) => setStudents((r.data.users || []).filter((u) => u.role === "student")))
      .catch(() => setStudents([]));
  }, [load]);

  const archive = (g) =>
    api.patch(`/admin/groups/${g.id}`, { archived: !g.archived })
      .then(() => { toast.success(g.archived ? "Restored" : "Archived — their marks are kept"); load(); })
      .catch(() => toast.error("Could not change it"));

  return (
    <AdminShell title="Groups" subtitle="Cohorts you give work to — they change each year, your worksheets do not">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
        <label className="text-sm text-slate-600 flex items-center gap-2">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Show past years
        </label>
        <button onClick={() => setEditing(null)}
          className="inline-flex items-center gap-1.5 bg-slate-800 text-white text-sm font-semibold rounded-lg px-3.5 py-2">
          <Plus size={15} /> New group
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading && rows.length === 0 && (
          <p className="py-14 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={15} className="animate-spin" /> Loading…
          </p>
        )}
        {!loading && rows.length === 0 && (
          <div className="py-14 text-center">
            <Users size={26} className="mx-auto text-slate-300 mb-3" />
            <p className="font-semibold text-slate-600">No groups yet</p>
            <p className="text-sm text-slate-400 mt-1">Make one per class, per year.</p>
          </div>
        )}
        {rows.map((g) => (
          <div key={g.id} className={`px-4 py-3.5 border-b border-slate-100 last:border-b-0 flex flex-wrap items-center gap-x-3 gap-y-2 hover:bg-slate-50/60 ${g.archived ? "opacity-60" : ""}`}>
            <button onClick={() => setEditing(g)} className="flex-1 min-w-[150px] text-left">
              <span className="block font-medium text-slate-900">
                {g.name} {g.archived && <span className="text-xs font-normal text-slate-400">· past year</span>}
              </span>
              <span className="block text-xs text-slate-500">
                {g.academic_year ? `${g.academic_year} · ` : ""}{g.student_count} student{g.student_count === 1 ? "" : "s"}
                {g.times_used > 0 ? ` · ${g.times_used} assignment${g.times_used === 1 ? "" : "s"} given` : ""}
              </span>
              {g.student_names?.length > 0 && (
                <span className="block text-xs text-slate-400 mt-0.5 truncate">{g.student_names.join(", ")}</span>
              )}
            </button>
            <button onClick={() => setEditing(g)}
              className="text-xs border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-white">Edit</button>
            <button onClick={() => archive(g)} title={g.archived ? "Restore" : "Archive (marks are kept)"}
              className="text-xs border border-slate-300 rounded-lg px-2 py-1.5 hover:bg-white text-slate-500">
              <Archive size={12} />
            </button>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-400 mt-4">
        Archiving a group hides it from this list. Nothing is deleted — the students, their answers and their
        marks stay, so you can still compare this year with last.
      </p>

      {editing !== undefined && (
        <Editor group={editing} students={students} onClose={() => setEditing(undefined)} onSaved={load} />
      )}
    </AdminShell>
  );
}
